import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@source/hooks/useStateHook';
import { getRecaptchaToken } from '@design-system/utils/utils';
import useRegionConfig from '@source/hooks/useRegionConfig';
import * as actions from '@source/redux/slice/otp';
import { IOTTRequestPayload, IRequestOTPPayload, IVerifyOTPRequestPayload } from '@source/interface/otp';
import OTP from '@source/services/OTP';

const useOTPService = () => {
  const dispatch = useAppDispatch();
  const otpState = useAppSelector((state) => state.otp, shallowEqual);

  const {
    countryConfig: { country },
  } = useRegionConfig();
  const { isReady, pathname, query } = useRouter();

  const { ticketSlug } = query;

  // Current lang
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  /** Actions */

  const requestOTP = useCallback(
    ({ payload }: { payload: IRequestOTPPayload }) => {
      const currentPage = pathname.replace(/[^a-zA-Z]/g, '');
      const recaptchaAction = `${country}_${currentPage}_requestOtp`;

      const requestOTPWithToken = (token: string) =>
        dispatch(
          actions.requestOTP({
            country: country || 'sg',
            payload: {
              ...payload,
              locale: currentLang,
              g_recaptcha_response: token,
              g_recaptcha_action: recaptchaAction,
              ticket_slug: (ticketSlug as string) || undefined,
            },
          }),
        );
      return getRecaptchaToken(requestOTPWithToken, requestOTPWithToken, recaptchaAction);
    },
    [dispatch, isReady, currentLang, ticketSlug],
  );

  const verifyOTP = useCallback(
    ({ payload, storeUser }: IVerifyOTPRequestPayload) =>
      dispatch(
        actions.verifyOTP({
          country: country || 'sg',
          payload: { ...payload, locale: currentLang, ticket_slug: (ticketSlug as string) || undefined },
          storeUser,
        }),
      ),
    [dispatch, currentLang, ticketSlug],
  );

  const requestOTT = useCallback(
    (payload?: IOTTRequestPayload) =>
      dispatch(actions.requestOTT(payload ? { ...payload, ticket_slug: ticketSlug as string } : undefined)),
    [dispatch, ticketSlug],
  );

  const getPaymentLink = useCallback(
    ({ ticketSlug, phone }: { ticketSlug: string; phone: string }) => {
      return OTP.getPaymentLink({ country: country || 'sg', ticketSlug, phone });
    },
    [country],
  );

  const otpCountdownDone = useCallback(() => dispatch(actions.otpCountdownDone()), [dispatch]);
  const resetOTPState = useCallback(() => dispatch(actions.resetState()), [dispatch]);

  const setInternationalPhone = useCallback(
    (phone: string | null) => dispatch(actions.setInternationalPhone(phone)),
    [dispatch],
  );
  const setPhone = useCallback((phone: string | null) => dispatch(actions.setPhone(phone)), [dispatch]);
  const setType = useCallback((type: string | null) => dispatch(actions.setType(type)), [dispatch]);
  const clearError = useCallback(() => dispatch(actions.clearError()), [dispatch]);

  return {
    otpState,
    requestOTP,
    verifyOTP,
    requestOTT,
    getPaymentLink,
    otpCountdownDone,
    resetOTPState,
    setPhone,
    setInternationalPhone,
    setType,
    clearError,
  };
};
export default useOTPService;
